/**
 * Classifies a failure of the in-app PDF preview (react-pdf / PDF.js).
 *
 * "unsupported": the viewer itself could not start — its lazily loaded chunk
 * did not load, the PDF.js worker could not be set up, or PDF.js called a
 * named built-in the browser lacks (Safari / iOS 18 is our floor, so this
 * should be rare). The viewer offers a download instead; breadcrumb only.
 *
 * "bad-pdf": a problem with the PDF itself (invalid, malformed,
 * password-protected) — the normal error state; breadcrumb only.
 *
 * "network": the PDF could not be fetched (an HTTP error, or offline — which
 * is a supported mode) — the normal error state; breadcrumb only.
 *
 * "cancelled": PDF.js aborted a load or superseded a page render (unmount,
 * URL change, fast zoom / scroll) — ignore.
 *
 * "unexpected": anything else, including ordinary bugs such as
 * "undefined is not an object" — report it.
 *
 * Deliberately narrow: generic TypeError / ReferenceError messages are not
 * treated as "unsupported", so real viewer bugs still reach Sentry.
 */
export type PdfPreviewFailure = "unsupported" | "bad-pdf" | "network" | "cancelled" | "unexpected";

// pdfjs-dist 6 error names. MissingPDFException / UnexpectedResponseException
// no longer exist there (HTTP errors are ResponseException), and react-pdf 11
// only runs pdf.js 6, so they are not listed.
const BAD_PDF_ERRORS = new Set(["InvalidPDFException", "PasswordException", "FormatError"]);
const CANCELLED_ERRORS = new Set(["AbortException", "RenderingCancelledException"]);

/** fetch() network failures: Chrome, Safari / WebKit, Firefox. Exact, so a
 * failed dynamic import ("Failed to fetch dynamically imported module")
 * stays a chunk-load failure. */
const isFetchFailure = (message: string): boolean =>
    message === "Failed to fetch" ||
    message === "Load failed" ||
    message.startsWith("NetworkError when attempting to fetch resource");

const CHUNK_LOAD = [
    /failed to fetch dynamically imported module/i,
    /importing a module script failed/i,
    /error loading dynamically imported module/i,
    /loading (css )?chunk .* failed/i,
];

/**
 * Built-ins PDF.js 6 relies on that a pre-floor browser may lack. The error
 * message must name one of them ("Promise.withResolvers is not a function",
 * "Can't find variable: Iterator").
 */
const MISSING_BUILT_IN =
    /\b(withResolvers|fromBase64|toBase64|fromHex|toHex|Iterator|groupBy|fromAsync|Float16Array)\b/;

const nameOf = (error: unknown): string =>
    typeof error === "object" && error !== null && "name" in error && typeof error.name === "string"
        ? error.name
        : "";

const messageOf = (error: unknown): string =>
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
        ? error.message
        : typeof error === "string"
          ? error
          : "";

export function classifyPdfPreviewError(error: unknown): PdfPreviewFailure {
    const name = nameOf(error);
    if (BAD_PDF_ERRORS.has(name)) return "bad-pdf";
    if (CANCELLED_ERRORS.has(name)) return "cancelled";
    if (name === "ResponseException") return "network";
    const message = messageOf(error);
    if (name === "TypeError" && isFetchFailure(message)) return "network";
    if (name === "ChunkLoadError" || CHUNK_LOAD.some((re) => re.test(message)))
        return "unsupported";
    // PDF.js could not start its worker (any error class).
    if (/setting up fake worker failed/i.test(message)) return "unsupported";
    if ((name === "TypeError" || name === "ReferenceError") && MISSING_BUILT_IN.test(message))
        return "unsupported";
    return "unexpected";
}

/**
 * Resolve a lazily imported module, or `fallback` when the import fails.
 * Used for the PDF viewer chunk so a browser that cannot run it gets the
 * download fallback instead of a crashed screen. `onFallback` receives the
 * error (to report it), and is not called on success.
 */
export function loadOrFallback<T>(
    loader: () => Promise<T>,
    fallback: T,
    onFallback?: (error: unknown) => void,
): Promise<T> {
    return loader().catch((error: unknown) => {
        onFallback?.(error);
        return fallback;
    });
}
